import moment from "moment";
import { buildListingRequestFields } from "./listingRequestEmail";
import { normalizeContact } from "./contactNormalize";
import { loadListingRequestRecipients } from "./notificationSettings";
import { flagRecipients } from "./accountingReview";
import { canSeeCommissions, actsAsClientCare } from "./roles";
import { ensureFollowUpPlan } from "./followUpPlansStore";
import { authedFetch } from "./authedFetch";

// utils/formatNumber.js
export function formatNumberShort(value) {
  if (value === null || value === undefined) return "0";

  const absValue = Math.abs(value);
  let number,
    suffix = "";

  if (absValue >= 1_000_000_000) {
    number = value / 1_000_000_000;
    suffix = "B";
  } else if (absValue >= 1_000_000) {
    number = value / 1_000_000;
    suffix = "M";
  } else if (absValue >= 1_000) {
    number = value / 1_000;
    suffix = "k";
  } else {
    number = value;
  }

  // 👇 floor instead of round
  // number = Math.floor(number * 10) / 10;
  number = Math.floor(number * 100) / 100;

  return `${number % 1 === 0 ? number : number.toFixed(2)}${suffix}`;
}

export const formatNumber = (value) =>
  value ? Number(value).toLocaleString("en-US") : "";

// Commission math input: a rate or amount that may legitimately be absent
// (husk deals, deals predating a field, empty inputs). One undefined operand
// turns a whole .toFixed(2) chain into the string "NaN" in Firestore, so
// anything non-numeric means zero here.
export const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Google Places appends ", USA" to addresses — strip it for display/storage.
// Global, so addresses embedded mid-sentence (notifications etc.) get cleaned.
export const stripCountry = (s) =>
  typeof s === "string" ? s.replace(/,\s*(USA|United States)\b/gi, "") : s;

// Title Case for UI labels — capitalizes every significant word, keeping
// short connectors lowercase unless they're the first or last word.
// Leaves the rest of each word untouched so names like "McGregor" survive.
const TITLE_CONNECTORS = new Set([
  "a", "an", "the", "in", "of", "to", "for", "with", "and", "or", "at", "on", "by",
]);
export function titleCaseLabel(s) {
  const words = String(s || "").split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const isConnector = TITLE_CONNECTORS.has(w.toLowerCase());
      if (i !== 0 && i !== words.length - 1 && isConnector) return w.toLowerCase();
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

export const removeCommas = (value) => value.replace(/,/g, "");

export const formatCurrency = (value, currency = "USD") => {
  if (value === null || value === undefined || isNaN(value)) return "$0.00";

  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// House date format is month/date/year with slashes, e.g. 07/22/2026.
export const formatDate = (date, format = "MM/DD/YYYY") => {
  if (!date) return "-";
  return moment(date).format(format);
};

// utils/statusColors.js
export const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "bg-emerald-500"; // bright green

    case "Under Contract":
      return "bg-sky-500"; // light blue

    case "Pending":
      return "bg-yellow-400"; // bright yellow

    case "Closed":
      return "bg-slate-500"; // neutral gray

    case "Cancelled":
      return "bg-rose-500"; // strong red/pink

    case "Expired":
      return "bg-orange-500"; // orange

    case "Prospect":
      return "bg-themeColor"; // purple

    case "On Hold":
      return "bg-amber-600"; // dark amber (different from yellow)

    case "CMAs":
      return "bg-themeColor"; // deep indigo (different from blue)

    case "Listing Appointment":
      return "bg-fuchsia-500"; // pink-purple

    case "Pre-Market":
      return "bg-lime-500"; // lime green (different from emerald)

    case "Coming Soon":
      return "bg-cyan-500"; // cyan (different from sky)

    default:
      return "bg-gray-400";
  }
};

export const formatText = (value) => {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const newmessagesuccess = async (
  title,
  id,
  message,
  taskid,
  type,
  dealId,
) => {
  try {
    // const res = await fetch('https://us-central1-raftekpro-82bb0.cloudfunctions.net/api/newslot', {
    const res = await authedFetch(
      // "https://team-edelstein.web.app/newmessage",
      "https://us-central1-team-edelstein.cloudfunctions.net/api/newmessage",

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
          message: message,
          id: id,
          taskid: taskid,
          dealid: dealId,
          type: type,
        }),
      },
    );

    if (res.status === 200) {
      console.log("new message sent " + JSON.stringify(res));
      const data = await res.json();

      console.log("Successfully Notified User");
    } else {
      console.log("Notififcation Error" + JSON.stringify(res));
    }
  } catch (error) {
    console.error("newmessagesuccess error:", error);
  }
};

export const formatDates = (date) => {
  if (!date || date === "") return "";

  const [yyyy, mm, dd] = date?.split("-");
  return `${mm}/${dd}/${yyyy}`;
}

// ============ DEAL APPROVAL NOTIFICATIONS ============
// User type values: 0 active, 1 deleted, 2 disabled.

export function selectApprovers(users) {
  return (users || []).filter(
    (u) =>
      u?.type !== 1 &&
      u?.type !== 2 &&
      (u?.role === "Admin" || u?.role === "Supervisor"),
  );
}

// Client Care duties audience: Supervisors plus Agents with the owner-set
// "Client care duties" switch (actsAsClientCare handles the active check).
export function selectClientCare(users) {
  return (users || []).filter((u) => actsAsClientCare(u));
}

export function selectAccountants(users) {
  return (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && u?.role === "Accountant",
  );
}

// Stage 1: notify the right audience when a pending deal is submitted.
// Recipients and title vary by requestType:
//   - "saleTurnIn" → Admins + Client Care (Supervisors) review and approve
//   - file request (default) → Client Care only handles the file
// Safe to call even if there are no recipients (no-op).
export async function notifyApprovers(deal, allUsers, submitter) {
  const isSaleTurnIn = deal?.requestType === "saleTurnIn";
  const recipients = isSaleTurnIn
    ? selectApprovers(allUsers)
    : selectClientCare(allUsers);
  if (recipients.length === 0) return;
  const submitterName =
    `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
    "An agent";
  const subject =
    deal?.propertyaddress ||
    (deal?.dealtype ? `${deal.dealtype} request` : "New deal");
  const message = `${submitterName} · ${subject}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  const title = isSaleTurnIn
    ? "New Deal Pending Approval"
    : "New Listing — File to Be Created";
  const notifType = isSaleTurnIn ? "pending-approval" : "pending-file";
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(title, u.id, message, dealRef, notifType, dealRef),
    ),
  );
}

// Client Care needs to know whenever a deal is turned in — including when a
// Team Lead / Supervisor / Accountant turns one in, which auto-approves it and
// therefore never hits the "pending approval" path above. Only fires for
// saleTurnIn deals, and skips the submitter so they don't notify themselves.
export async function notifyClientCareTurnIn(deal, allUsers, submitter) {
  if (deal?.requestType !== "saleTurnIn") return;
  const recipients = selectClientCare(allUsers).filter(
    (u) => u?.id !== submitter?.id,
  );
  if (recipients.length === 0) return;
  const submitterName =
    `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
    "A team lead";
  const subject =
    deal?.propertyaddress ||
    (deal?.dealtype ? `${deal.dealtype} deal` : "a deal");
  const message = `${submitterName} turned in ${subject}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "New Deal Turned In",
        u.id,
        message,
        dealRef,
        "deal-turned-in",
        dealRef,
      ),
    ),
  );
}

// Single entry point for post-submit notifications, so every turn-in path stays
// consistent. Pending submissions go to the approver / file-request audience
// (unchanged). An already-approved turn-in — e.g. a Team Lead turning in their
// own deal, which used to notify no one — informs Client Care instead.
export async function notifyOnDealSubmit(deal, allUsers, submitter) {
  // Role-based in-app alerts: the approval queue (pending) or Client Care
  // (already-approved turn-in).
  if (deal?.status === "Pending") {
    await notifyApprovers(deal, allUsers, submitter);
  } else {
    await notifyClientCareTurnIn(deal, allUsers, submitter);
    // Auto-approved turn-ins never hit the Pending→Approved flip that alerts
    // accounting, so the accountants are told here instead.
    if (deal?.requestType === "saleTurnIn") {
      await notifyAccountantsOnApproval(deal, allUsers, submitter);
    }
  }
  // Sale turn-ins also email the configured Request-Notification list for the
  // matching side (Settings → Notifications), so the office triage recipients
  // are looped in just like they are for new requests. Fire-and-forget.
  if (deal?.requestType === "saleTurnIn") {
    emailListingRequestRecipients(deal, submitter, { event: "turnIn" }).catch(
      (e) => console.error("Turn-in recipient email failed:", e),
    );
  }
}

// Notify when a brand-new pipeline lead is created. Recipients: the lead's
// assigned agent (if any) plus the configured Settings -> Notifications list
// for that side (seller/buyer) — the office triage queue. The person who
// created the lead is never notified about their own add, and deleted/disabled
// users are skipped. Reuses the existing /newmessage endpoint (no backend
// deploy). Safe no-op when there are no recipients. `side` is "seller" | "buyer".
export async function notifyOnNewLead(lead, allUsers, side) {
  const creatorId = lead?.createdby || "";
  const isBuyer = side === "buyer";
  let lists = { seller: [], buyer: [] };
  try {
    lists = await loadListingRequestRecipients();
  } catch (e) {
    /* no central list configured — fall back to assigned agent only */
  }
  const ids = new Set();
  if (lead?.assignedAgent) ids.add(lead.assignedAgent);
  (isBuyer ? lists.buyer : lists.seller).forEach((id) => ids.add(id));
  const targetIds = [...ids].filter((id) => {
    if (!id || id === creatorId) return false;
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const title = isBuyer ? "New Buyer Lead" : "New Listing Lead";
  const subject =
    lead?.contactName ||
    lead?.propertyAddress ||
    (isBuyer ? "New buyer lead" : "New listing lead");
  // The /newmessage endpoint requires non-empty taskid + dealid — use the lead id.
  const ref = String(lead?.id || lead?.createdat || Date.now());
  await Promise.all(
    targetIds.map((id) =>
      newmessagesuccess(title, id, subject, ref, "new-lead", ref),
    ),
  );
}

// Notify when a deal reaches a milestone stage. Recipients: the deal's assigned
// agent, plus Client Care (Supervisors) when a listing goes live / under
// contract (so the file gets built) and Accountants when it closes. The actor
// who made the change isn't notified about their own action. Reuses the
// existing endpoint. Safe no-op for non-milestone stages or no recipients.
const MILESTONE_LABELS = {
  "Pre-Market": "Listing Signed",
  "Coming Soon": "Listing Coming Soon",
  "Under Contract": "Deal Under Contract",
  Closed: "Deal Closed",
};
export async function notifyDealMilestone(deal, newStage, allUsers, actor) {
  const label = MILESTONE_LABELS[newStage];
  if (!label) return; // not a milestone we push on
  // A closing starts the Past Client Follow-Ups schedule. Fire-and-forget:
  // a follow-up plan failure must never block the milestone notification.
  if (newStage === "Closed") {
    ensureFollowUpPlan(deal).catch((e) =>
      console.error("Follow-up plan creation failed:", e),
    );
  }
  const actorId = actor?.id || "";
  const ids = new Set();
  if (deal?.agentData) ids.add(deal.agentData);
  if (["Pre-Market", "Coming Soon", "Under Contract"].includes(newStage))
    selectClientCare(allUsers).forEach((u) => ids.add(u.id));
  if (newStage === "Closed")
    selectAccountants(allUsers).forEach((u) => ids.add(u.id));
  const targetIds = [...ids].filter((id) => {
    if (!id || id === actorId) return false;
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const subject =
    deal?.propertyaddress || deal?.clientLastName || "Deal updated";
  const ref = String(deal?.createdat || deal?.id || Date.now());
  await Promise.all(
    targetIds.map((id) =>
      newmessagesuccess(label, id, subject, ref, "deal-milestone", ref),
    ),
  );
}

// Notify the requesting agent when their file request has been completed by Client Care.
// Safe to call even if the recipient lookup fails (no-op).
export async function notifyAgentOnFileComplete(deal, allUsers, completer) {
  const recipientId = deal?.createdby;
  if (!recipientId) return;
  const recipient = (allUsers || []).find((u) => u?.id === recipientId);
  if (!recipient || recipient.type === 1 || recipient.type === 2) return;
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  // A buyer file has no property yet, so the address is blank and this read a
  // bare "Your file" with nothing to identify it. The client's name is on the
  // deal — same leak as the queue rows and the Drive folder names.
  const subject =
    deal?.propertyaddress || deal?.clientLastName || "Your file";
  const message = `${subject} · completed by ${completerName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await newmessagesuccess(
    "Your file is ready",
    recipientId,
    message,
    dealRef,
    "file-completed",
    dealRef,
  );
}

// Notify the approving LC (or the admin/supervisor fallback queue) that an agent
// requested an up-for-grabs open house. `opportunity.approverLcId` was resolved
// at post time; when blank we notify active Admins + Supervisors.
export async function notifyLcOnOhRequest(opportunity, request, allUsers) {
  const recipientIds = opportunity?.approverLcId
    ? [opportunity.approverLcId]
    : selectApprovers(allUsers).map((u) => u.id);
  const targetIds = recipientIds.filter((id) => {
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const addr = opportunity?.propertyAddress?.split(",")[0] || "a listing";
  const title = "Open House Request to Approve";
  const message = `${request?.agentName || "An agent"} wants to host ${addr}`;
  const ref = String(opportunity?.id || Date.now());
  await Promise.all(
    targetIds.map((id) => newmessagesuccess(title, id, message, ref, "oh-request", ref)),
  );
}

// Notify a requesting agent that their request was approved or declined.
// `decision` is "approved" | "declined".
export async function notifyAgentOnOhDecision(opportunity, request, decision) {
  const agentId = request?.agentId;
  if (!agentId) return;
  const addr = opportunity?.propertyAddress?.split(",")[0] || "the listing";
  const approved = decision === "approved";
  const title = approved ? "You're Hosting an Open House" : "Open House Request Update";
  const message = approved
    ? `You're approved to host ${addr}`
    : `Your request for ${addr} wasn't selected this time`;
  const ref = String(opportunity?.id || Date.now());
  await newmessagesuccess(
    title,
    agentId,
    message,
    ref,
    approved ? "oh-approved" : "oh-declined",
    ref,
  );
}

// Email the agent listed on a file (deal.agentData) when Client Care marks it
// complete. Additive to notifyAgentOnFileComplete, which sends an in-app
// notification to the submitter (deal.createdby). The Cloud Function sends the
// email AS the completer, so the completer must have a connected Gmail token.
//
// Returns a result the caller uses to pick a toast:
//   { ok: true, gmailMessageId? }
//   { ok: false, reason: "no-recipient" }       // no agentData / not found / no email
//   { ok: false, reason: "no-gmail" }            // completer has no gmail token
//   { ok: false, reason: "send-failed", error }  // endpoint returned an error
export async function emailAgentOnFileComplete(deal, allUsers, completer) {
  const agentId = deal?.agentData;
  if (!agentId) return { ok: false, reason: "no-recipient" };

  const agent = (allUsers || []).find((u) => u?.id === agentId);
  if (!agent || !agent.email) return { ok: false, reason: "no-recipient" };

  if (!completer?.gmailtoken?.access_token) {
    return { ok: false, reason: "no-gmail" };
  }

  const { sendOpenHouseEmail } = await import("./emailSender");

  const agentFirst = agent.firstname || "there";
  const agentFullName =
    `${agent.firstname || ""} ${agent.lastname || ""}`.trim() || "Agent";
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  const address = deal?.propertyaddress || "";
  const subject = address
    ? `File Complete — ${address}`
    : "Your File Is Complete";
  const propertyPhrase = address ? `your file for ${address}` : "your file";

  const text = [
    `Hi ${agentFirst},`,
    ``,
    `Good news — ${propertyPhrase} has been completed by ${completerName}.`,
    `No action is needed on your end.`,
    ``,
    `— Team Edelstein`,
  ].join("\n");

  const html =
    `<p>Hi ${agentFirst},</p>` +
    `<p>Good news — ${propertyPhrase} has been completed by ${completerName}.</p>` +
    `<p>No action is needed on your end.</p>` +
    `<p>— Team Edelstein</p>`;

  const result = await sendOpenHouseEmail({
    userId: completer.id,
    to: agent.email,
    toName: agentFullName,
    subject,
    text,
    html,
    type: "file-completed",
    relatedDealId: String(deal?.createdat || deal?.id || ""),
  });

  if (result?.ok) return { ok: true, gmailMessageId: result.gmailMessageId };
  return { ok: false, reason: "send-failed", error: result?.error };
}

// Email the configured "listing/buyer request" recipients (set in Settings →
// Notifications) when a new request is submitted. Independent of and
// additional to the in-app notifyApprovers flow.
export async function emailListingRequestRecipients(deal, submitter, opts = {}) {
  // Lazy imports to avoid a circular dependency (helper.js is widely imported).
  const { loadListingRequestRecipients } = await import(
    "./notificationSettings"
  );
  const { sendOpenHouseEmail } = await import("./emailSender");
  const { getAllusers, getContactsByIds } = await import(
    "./firebasefunctions"
  );

  try {
    const lists = await loadListingRequestRecipients();
    const isBuyer = deal?.dealtype === "Buyer";
    const isTurnIn = opts.event === "turnIn";
    // Turn-ins email the whole staff group (both sides combined), so a blank
    // per-side list can never silently drop the notification. Requests stay
    // per-side (buyer requests → buyer list, listing requests → seller list).
    const userIds = isTurnIn
      ? [...new Set([...(lists.buyer || []), ...(lists.seller || [])])]
      : isBuyer
        ? lists.buyer
        : lists.seller;
    if (!userIds || userIds.length === 0) return;

    const allUsers = await getAllusers("users");
    const recipients = (allUsers || []).filter(
      (u) =>
        userIds.includes(u.id) &&
        u.type !== 1 &&
        u.type !== 2 &&
        u.email,
    );
    if (recipients.length === 0) return;

    const submitterName =
      `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
      "An agent";
    const agent = (allUsers || []).find((u) => u?.id === deal?.agentData);
    const agentName = agent
      ? `${agent.firstname || ""} ${agent.lastname || ""}`.trim()
      : "";
    const address = deal?.propertyaddress || "(no address provided)";
    const eventLabel = isTurnIn
      ? "Sale Turn-In"
      : isBuyer
        ? "Buyer Request"
        : "Listing Request";
    // Buyer requests have no property yet — anchor the subject on the client
    // name instead of "(no address provided)".
    const subject = `New ${eventLabel} — ${
      deal?.propertyaddress || deal?.clientLastName || address
    }`;

    // Resolve attached clients (deal.customers = contact ids) to name/phone/
    // email for the body. Best-effort: on failure we still send every other
    // field rather than dropping the whole email.
    let clients = [];
    try {
      const ids = (deal?.customers || []).filter(Boolean);
      if (ids.length > 0) {
        const raw = await getContactsByIds(ids);
        clients = (raw || []).map((c) => normalizeContact(c)).filter(Boolean);
      }
    } catch (clientErr) {
      console.error("Request email: failed to resolve clients:", clientErr);
    }

    let lines;
    let htmlLines;
    if (isTurnIn) {
      // Turn-ins carry sale fields, not request-intake fields — keep the email
      // short: who turned in what, for whom.
      const clientName = clients
        .map((c) => c?.name)
        .filter(Boolean)
        .join(" & ");
      const sideWord = isBuyer ? "buyer-side" : "listing-side";
      lines = [
        `${submitterName} just turned in a ${sideWord} sale.`,
        ``,
        `Address: ${address}`,
        clientName ? `Client: ${clientName}` : null,
        agentName ? `Agent: ${agentName}` : null,
        ``,
        `Open in the CRM to review the deal.`,
      ].filter((l) => l !== null);
      htmlLines = [
        `<p>${submitterName} just turned in a ${sideWord} sale.</p>`,
        `<ul>`,
        `<li><strong>Address:</strong> ${address}</li>`,
        clientName ? `<li><strong>Client:</strong> ${clientName}</li>` : "",
        agentName ? `<li><strong>Agent:</strong> ${agentName}</li>` : "",
        `</ul>`,
        `<p>Open in the CRM to review the deal.</p>`,
      ].filter(Boolean);
    } else {
      // Ordered [{ label, value }] rows — every filled-in intake field, blanks
      // omitted. formatDate is this file's own moment-based formatter.
      const fields = buildListingRequestFields({
        deal,
        clients,
        agentName,
        formatDate,
      });
      lines = [
        `${submitterName} just submitted a new ${eventLabel}.`,
        ``,
        ...fields.map((f) => `${f.label}: ${f.value}`),
        ``,
        `Open in the CRM to start the file.`,
      ];
      htmlLines = [
        `<p>${submitterName} just submitted a new ${eventLabel}.</p>`,
        `<ul>`,
        ...fields.map((f) => `<li><strong>${f.label}:</strong> ${f.value}</li>`),
        `</ul>`,
        `<p>Open in the CRM to start the file.</p>`,
      ];
    }
    const body = lines.join("\n");
    const html = htmlLines.join("");

    // sendOpenHouseEmail RESOLVES { ok: false } on failure rather than throwing,
    // so the old bare .catch() here never ran and a failed email vanished with no
    // retry and no record. Check the result, retry once, and report what failed.
    const { sendWithRetry } = await import("./sendWithRetry");
    const results = await Promise.all(
      recipients.map(async (r) => ({
        email: r.email,
        result: await sendWithRetry(() =>
          sendOpenHouseEmail({
            userId: submitter?.id,
            to: r.email,
            toName: `${r.firstname || ""} ${r.lastname || ""}`.trim(),
            subject,
            text: body,
            html,
            type: isTurnIn ? "sale-turnin-notification" : "listing-request-notification",
            relatedDealId: deal?.createdat || deal?.id || "",
          }),
        ),
      })),
    );

    const failed = results.filter((r) => !r.result?.ok);
    if (failed.length > 0) {
      console.error(
        `Request email: ${failed.length} of ${results.length} recipients did not receive "${subject}" -`,
        failed.map((f) => `${f.email} (${f.result?.error || "unknown"})`).join(", "),
      );
    }
    return { sent: results.length - failed.length, failed: failed.length };
  } catch (err) {
    console.error("emailListingRequestRecipients failed:", err);
    return { sent: 0, failed: 0, error: err?.message };
  }
}

// Builds the full commission-breakdown email accountants get when a deal
// enters their review queue. Zero rows are skipped; amounts are the stored
// .toFixed(2) strings so formatCurrency handles them. Exported for tests.
export function buildAccountingApprovalEmail(deal, approverName, origin) {
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "$0.00";
  };
  const address = deal?.propertyaddress || "New deal";
  const link = `${origin || ""}/#/dashboard/commission-approvals`;
  const closing = deal?.closingdate ? formatDates(deal.closingdate) : "";
  const splitLine = deal?.fixedagentcommission
    ? `flat ${money(deal?.agentCommission)}`
    : `${Number(deal?.agentCommission) || 0}%`;

  const rows = [
    ["Agent", deal?.agentName || ""],
    ["Deal type", deal?.dealtype || ""],
    ["Closing date", closing],
    // The number every commission below is derived from. Accounting were
    // getting the results without the input. Stored as `offerPrice`; the deal
    // forms label it "Sales Price". Blank when not filled in yet rather than
    // "$0.00", which would read as a genuine zero-dollar sale.
    ["Sales price", Number(deal?.offerPrice) > 0 ? money(deal?.offerPrice) : ""],
    // The deal's commission rate (e.g. 2.7%, 3%) — the other input accounting
    // needs next to the sales price to sanity-check every dollar row below.
    [
      "Commission rate",
      Number(deal?.totalcommissionrate) > 0
        ? `${Number(deal.totalcommissionrate)}%`
        : "",
    ],
    ["Split type", deal?.splitdescription || ""],
    ["Cap type", deal?.captypedescription || ""],
    ["Agent split", splitLine],
    ["Total commission", money(deal?.totalCommissionamount)],
    ["CBR (broker)", Number(deal?.bankcommission) > 0 ? money(deal?.bankcommission) : ""],
    ["Broker admin fee", Number(deal?.brokeradminfee) > 0 ? money(deal?.brokeradminfee) : ""],
    ["Referral agent", Number(deal?.thirdpartyagentcommissionamount) > 0 ? money(deal?.thirdpartyagentcommissionamount) : ""],
    ["Lead source", Number(deal?.leadsourceCommissionamount) > 0 ? money(deal?.leadsourceCommissionamount) : ""],
    ["Agent gets", money(deal?.agentCommissionamount)],
    ["Team lead gets", money(deal?.teamleadCommissionamount)],
    ["Team take-home", money(deal?.takehomecommissionamount)],
  ].filter(([, v]) => v !== "");

  const subject = `Deal approved for accounting review: ${address}`;
  const text =
    `${address} was approved by ${approverName}.\n\n` +
    rows.map(([k, v]) => `${k}: ${v}`).join("\n") +
    `\n\nReview and approve it here: ${link}`;
  const html = `
  <div style="font-family:Arial,Helvetica,sans-serif;max-width:560px;margin:0 auto;color:#0f172a">
    <div style="background:#0f172a;color:#ffffff;border-radius:12px 12px 0 0;padding:18px 24px">
      <div style="font-size:12px;letter-spacing:.08em;text-transform:uppercase;opacity:.7">Team Edelstein · Accounting</div>
      <div style="font-size:18px;font-weight:bold;margin-top:4px">${address}</div>
      <div style="font-size:13px;opacity:.8;margin-top:2px">Approved by ${approverName}. Ready for your review.</div>
    </div>
    <div style="border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;padding:8px 24px 24px">
      <table style="width:100%;border-collapse:collapse;font-size:14px">
        ${rows
          .map(
            ([k, v]) =>
              `<tr><td style="padding:8px 0;color:#64748b;border-bottom:1px solid #f1f5f9">${k}</td><td style="padding:8px 0;text-align:right;font-weight:bold;border-bottom:1px solid #f1f5f9">${v}</td></tr>`,
          )
          .join("")}
      </table>
      <a href="${link}" style="display:block;background:#0f172a;color:#ffffff;text-decoration:none;text-align:center;border-radius:10px;padding:12px 0;font-size:14px;font-weight:bold;margin-top:18px">Open Commission Approvals</a>
    </div>
  </div>`;
  return { subject, text, html, link };
}

// Stage 2: notify Accountants when a deal transitions to Approved — an in-app
// push AND an email with the full commission breakdown linking to the
// approvals page (Henry, 2026-07-31). Caller must guard with the prior-status
// check (see plan tasks 8 and 9). Email failures never block the push.
export async function notifyAccountantsOnApproval(deal, allUsers, approver) {
  const recipients = selectAccountants(allUsers).filter(
    (u) => u?.id !== approver?.id,
  );
  if (recipients.length === 0) return;
  const approverName =
    `${approver?.firstname || ""} ${approver?.lastname || ""}`.trim() ||
    "An approver";
  const subject = deal?.propertyaddress || "Deal";
  const message = `${subject} · approved by ${approverName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "Deal ready for accounting review",
        u.id,
        message,
        dealRef,
        "approved-for-accounting",
        dealRef,
      ),
    ),
  );
  try {
    const origin =
      (typeof window !== "undefined" && window.location && window.location.origin) ||
      "";
    const email = buildAccountingApprovalEmail(deal, approverName, origin);
    const { sendOpenHouseEmail } = await import("./emailSender");
    await Promise.all(
      recipients
        .filter((u) => u?.email)
        .map((u) =>
          sendOpenHouseEmail({
            userId: approver?.id || "",
            to: u.email,
            toName: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
            subject: email.subject,
            text: email.text,
            html: email.html,
            type: "accounting-approval-notification",
            relatedDealId: dealRef,
          }).catch((err) =>
            console.error("Accounting email failed:", u.email, err),
          ),
        ),
    );
  } catch (err) {
    console.error("Accounting approval email failed:", err);
  }
}

// Stage 3: an accountant flagged a deal's numbers. Alert the coordinator who
// approved it (or the submitter, for auto-approved turn-ins) plus the brokerage
// owner, so someone fixes the figures. The flag itself lives on the deal doc.
export async function notifyCommissionFlagged(deal, allUsers, flagger, note) {
  const recipients = flagRecipients(deal, allUsers, flagger);
  if (recipients.length === 0) return;
  const flaggerName =
    `${flagger?.firstname || ""} ${flagger?.lastname || ""}`.trim() ||
    "The accountant";
  const subject = deal?.propertyaddress || "A deal";
  // The note usually talks dollar figures, and a push lands on lock screens —
  // only recipients cleared for commissions (or the deal's own submitter, who
  // always sees their own numbers) get the note text itself.
  const fullBody = `${subject} · ${flaggerName}: ${note || "commission needs a look"}`;
  const genericBody = `${subject} · fix requested by ${flaggerName}. Open the deal for details.`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "Commission needs a fix",
        u.id,
        canSeeCommissions(u) || u?.id === deal?.createdby
          ? fullBody
          : genericBody,
        dealRef,
        "commission-flagged",
        dealRef,
      ),
    ),
  );
}
